import { Fragment, type ReactElement } from 'react';

import { useWindowSize } from '@/utils/WindowSizeProvider';

import type { NumberRange, RoomWithPeople } from '@/interfaces';
import { HotelRoomItem } from '../hotelRoomItem/HotelRoomItem';

export interface HotelRoomListProps {
    rooms: readonly RoomWithPeople[];
    updateRooms: (rooms: readonly RoomWithPeople[]) => void;
    roomLimits: NumberRange;
    adultLimits: NumberRange;
    childLimits: NumberRange;
    onDismiss: () => void;
}

export const HotelRoomList = ({ rooms, updateRooms, roomLimits, adultLimits, childLimits, onDismiss }: HotelRoomListProps) => {
    const { isMobile } = useWindowSize();

    const addRoom = (): void => {
        if (rooms.length < roomLimits[1]) {
            const newRooms = [...rooms];
            newRooms.push({ adults: 2, children: [] });
            updateRooms(newRooms);
        }
    };

    const removeRoom = (): void => {
        if (rooms.length > roomLimits[0]) {
            const newRooms = [...rooms];
            newRooms.pop();
            updateRooms(newRooms);
        }
    };

    const addAdult = (index: number): void => {
        if (rooms[index] && rooms[index].adults < adultLimits[1]) {
            const newRooms = [...rooms];
            newRooms[index] = { ...newRooms[index], adults: newRooms[index].adults + 1 };
            updateRooms(newRooms);
        }
    };

    const removeAdult = (index: number): void => {
        if (rooms[index] && rooms[index].adults > adultLimits[0]) {
            const newRooms = [...rooms];
            newRooms[index] = { ...newRooms[index], adults: newRooms[index].adults - 1 };
            updateRooms(newRooms);
        }
    };

    const setAdults = (index: number, adults: number): void => {
        if (rooms[index]) {
            const newRooms = [...rooms];
            newRooms[index] = {
                ...newRooms[index],
                adults: Math.max(Math.min(adults, adultLimits[1]), adultLimits[0]),
            };
            updateRooms(newRooms);
        }
    };

    const addChild = (index: number): void => {
        if (rooms[index] && rooms[index].children.length < childLimits[1]) {
            const newRooms = [...rooms];
            newRooms[index] = { ...newRooms[index], children: newRooms[index].children.concat(0) };
            updateRooms(newRooms);
        }
    };

    const removeChild = (index: number): void => {
        if (rooms[index] && rooms[index].children.length > childLimits[0]) {
            const newRooms = [...rooms];
            newRooms[index] = {
                ...newRooms[index],
                children: newRooms[index].children.slice(0, newRooms[index].children.length - 1),
            };
            updateRooms(newRooms);
        }
    };

    const setChildren = (index: number, children: number): void => {
        if (rooms[index]) {
            const newRooms = [...rooms];
            const newChildren = new Array(children).fill(0).slice(0, Math.max(Math.min(children, childLimits[1]), childLimits[0]));
            newRooms[index].children.forEach((childAge, childIndex) => {
                if (newChildren.length > childIndex) {
                    newChildren[childIndex] = childAge;
                }
            });
            newRooms[index] = { ...newRooms[index], children: newChildren };
            updateRooms(newRooms);
        }
    };

    const setChildAge = (roomIndex: number, childIndex: number, age: number): void => {
        if (rooms[roomIndex] && rooms[roomIndex].children.length > childIndex) {
            const newRooms = [...rooms];
            const children = [...newRooms[roomIndex].children];
            children[childIndex] = age;
            newRooms[roomIndex] = { ...newRooms[roomIndex], children };
            updateRooms(newRooms);
        }
    };

    const renderRooms = (): readonly ReactElement[] =>
        rooms.map((room, roomIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: This existed before biome was added
            <Fragment key={`hotel-room-${roomIndex}`}>
                <div
                    data-testid={`hotel-room-${roomIndex}`}
                    className={isMobile ? 'u-strong u-t3 u-mt32 u-ml16 u-mr16' : 'u-strong u-t3 u-mt16'}>
                    Rom {roomIndex + 1}
                </div>
                <HotelRoomItem
                    room={room}
                    adultLimits={adultLimits}
                    addAdult={() => addAdult(roomIndex)}
                    removeAdult={() => removeAdult(roomIndex)}
                    setAdults={(adults: number) => setAdults(roomIndex, adults)}
                    childLimits={childLimits}
                    addChild={() => addChild(roomIndex)}
                    removeChild={() => removeChild(roomIndex)}
                    setChildren={(children: number) => setChildren(roomIndex, children)}
                    setChildAge={(childIndex: number, age: number) => setChildAge(roomIndex, childIndex, age)}
                    roomIndex={roomIndex}
                    isMobile={isMobile}
                />
            </Fragment>
        ));

    function renderHotelRoomsMobile() {
        const invalidHotelRooms = rooms.some((room) => room.children.length + room.adults === 0);
        return (
            <div className="fullscreen-list u-bg-ice u-pv16">
                <div className="scrollable-list-container">
                    <div className="u-ma16 u-mt8 u-t2">Personer og rom</div>
                    {renderRooms()}
                    <div className="u-ma16 u-mt32 u-t2">
                        {rooms.length < roomLimits[1] && (
                            <button
                                type="button"
                                data-testid="add-room"
                                className={`button button--flat ${rooms.length > roomLimits[0] ? 'u-size1of2' : 'u-size1of1'}`}
                                onClick={addRoom}>
                                Legg til rom
                            </button>
                        )}
                        {rooms.length > roomLimits[0] && (
                            <button
                                type="button"
                                data-testid="remove-room"
                                className={`button button--flat ${rooms.length < roomLimits[1] ? 'u-size1of2' : 'u-size1of1'}`}
                                onClick={removeRoom}>
                                Fjern rom
                            </button>
                        )}
                    </div>
                    <div className="u-ma16 u-mb64">
                        <button
                            type="button"
                            disabled={invalidHotelRooms}
                            data-testid="dismiss-adding-travellers"
                            className="button button--cta u-size1of1 u-mt64"
                            onClick={onDismiss}>
                            Ferdig
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    function renderHotelRoomsDesktop() {
        return (
            <div onClick={(e) => e.stopPropagation()} className="dropdown-list">
                <div className="room-dropdown">
                    {renderRooms()}
                    <div className="u-valign-middle u-pb16">
                        {rooms.length < roomLimits[1] && (
                            <button
                                type="button"
                                data-testid="add-room"
                                className={`u-float-left button button--flat u-pa8 ${
                                    rooms.length > roomLimits[0] ? 'u-size1of2' : 'u-size1of1'
                                }`}
                                onClick={addRoom}>
                                Legg til rom
                            </button>
                        )}
                        {rooms.length > roomLimits[0] && (
                            <button
                                type="button"
                                data-testid="remove-room"
                                className={`u-float-right button button--flat u-pa8 ${
                                    rooms.length > roomLimits[0] ? 'u-size1of2' : 'u-size1of1'
                                }`}
                                onClick={removeRoom}>
                                Fjern rom
                            </button>
                        )}
                    </div>
                    <div onFocus={onDismiss} />
                </div>
            </div>
        );
    }

    return isMobile ? renderHotelRoomsMobile() : renderHotelRoomsDesktop();
};
